import { useRef, useState } from 'react'

import type { AuthMethods, StartEmailCodeResult } from '../../services/account/authApi'
import { getAuthErrorMessage } from '../../services/account/authErrors'
import { EmailCodeForm } from './EmailCodeForm'
import { errorClass, primaryButtonClass, secondaryButtonClass } from './styles'

interface SignInOptionsProps {
  methods: AuthMethods
  /** Passkeys are on server-side AND this device can use them. */
  passkeyAvailable: boolean
  onRequestEmailCode: (email: string) => Promise<StartEmailCodeResult>
  onVerifyEmailCode: (email: string, code: string) => Promise<void>
  onGoogleSignIn: () => Promise<void>
  onPasskeySignIn: () => Promise<void>
}

type SocialProvider = 'google' | 'passkey'

// The signed-out view. Email codes only appear once the backend reports them as
// available (`methods`), so that method can be switched off server-side without
// a release. Passkeys are offered first when the server has them on and this
// device can use one. Google is verified by Google and Firebase, so it doesn't
// depend on our backend. Sign in with Apple is offered in the mobile app only.
export function SignInOptions({
  methods,
  passkeyAvailable,
  onRequestEmailCode,
  onVerifyEmailCode,
  onGoogleSignIn,
  onPasskeySignIn,
}: SignInOptionsProps) {
  const [busyWith, setBusyWith] = useState<SocialProvider | null>(null)
  const [socialError, setSocialError] = useState<string | null>(null)
  // The ref is the real lock: state is stale within one tick, so two quick clicks
  // could otherwise open two sign-in windows. `busyWith` only drives the UI.
  const inFlight = useRef(false)

  const runSignIn = async (provider: SocialProvider, signIn: () => Promise<void>) => {
    // One at a time; a second click while the first prompt is open is ignored.
    if (inFlight.current) return
    inFlight.current = true
    setSocialError(null)
    setBusyWith(provider)
    try {
      await signIn()
    } catch (err) {
      setSocialError(getAuthErrorMessage(err))
    } finally {
      inFlight.current = false
      setBusyWith(null)
    }
  }

  const busy = busyWith !== null

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-bold">Sign in</h2>
        <p className="text-sm text-white/60 mt-1">
          {passkeyAvailable
            ? 'No password needed. Use a passkey, or we can email you a code.'
            : methods.emailOtp
              ? "No password needed. We'll email you a code, or you can use Google."
              : 'Sign in to keep your account with you.'}
        </p>
      </div>

      {passkeyAvailable && (
        <>
          <button
            type="button"
            disabled={busy}
            onClick={() => void runSignIn('passkey', onPasskeySignIn)}
            className={primaryButtonClass}>
            {busyWith === 'passkey' ? 'Waiting for your device...' : 'Sign in with a passkey'}
          </button>
          <p className="text-xs text-white/40 -mt-2">
            Uses your fingerprint, face or screen lock. Only works once you have added a passkey.
          </p>
          <div className="flex items-center gap-3 text-white/30 text-xs uppercase tracking-widest" aria-hidden="true">
            <span className="flex-1 h-px bg-white/10" />
            or
            <span className="flex-1 h-px bg-white/10" />
          </div>
        </>
      )}

      {methods.emailOtp && (
        <>
          <EmailCodeForm onRequestCode={onRequestEmailCode} onVerifyCode={onVerifyEmailCode} />
          <div className="flex items-center gap-3 text-white/30 text-xs uppercase tracking-widest" aria-hidden="true">
            <span className="flex-1 h-px bg-white/10" />
            or
            <span className="flex-1 h-px bg-white/10" />
          </div>
        </>
      )}

      {socialError && (
        <p role="alert" data-testid="social-error" className={errorClass}>
          {socialError}
        </p>
      )}

      <button
        type="button"
        disabled={busy}
        onClick={() => void runSignIn('google', onGoogleSignIn)}
        className={secondaryButtonClass}>
        {busyWith === 'google' ? 'Opening Google...' : 'Continue with Google'}
      </button>
    </div>
  )
}
